import React, { useState } from 'react';
import './TopWidgetEdit.css';
import { useWidgetEdit } from '../hooks/useWidgetEdit';
import { useUserProducts, getProductTypeInfo, useProductData } from '../hooks/useProductData';
import ImageUploadSection from '../WidgetEditComponents/ImageUploadSection';
import TextSettingsSection from '../WidgetEditComponents/TextSettingsSection';
import MoreSettingsSection from '../WidgetEditComponents/MoreSettingsSection';
import SliderSection from '../WidgetEditComponents/SliderSection';
import { ImageEffectSelector } from '../WidgetEditComponents/ImageEffectSelector';
import ToggleSwitch from '../WidgetEditComponents/ToggleSwitch';

function ProductInfoWidgetEdit({ widget, onUpdate, colors }) {
    const { editData, handleInputChange, uploadImage, handleSliderChange } = useWidgetEdit(widget, onUpdate);
    
    // Ensure showDiscountBadge has a default value
    const editDataWithDefaults = {
        ...editData,
        showDiscountBadge: editData.showDiscountBadge !== false // Default to true
    };
    const { products } = useUserProducts();
    
    // Get current product data to check for discounts
    const { productData } = useProductData(editData.productId);
    
    // Check if product has discounts
    const hasDiscount = productData && productData.showDiscount && productData.discountValue;
    const hasTimeLimitedDiscount = hasDiscount && productData.discountTimeLimited && productData.discountExpiryDate;

    // Custom onUpdate function that filters undefined values
    const customOnUpdate = (updatedData) => {
        const filteredData = Object.fromEntries(
            Object.entries(updatedData).filter(([key, value]) => value !== undefined)
        );
        onUpdate(filteredData);
    };

    // Upload States
    const [isUploadingBackground, setIsUploadingBackground] = useState(false);
    const [isUploadingProduct, setIsUploadingProduct] = useState(false);
    

    // Handle Effect Changes
    const handleEffectChange = (effectType, effectId) => {
        handleInputChange({ target: { name: effectType, value: effectId } });
    };

    // Filter products to only show digital and course products
    const validProducts = products.filter(product => 
        product.type === 'digital' || product.type === 'course'
    );

    // 🚀 REVOLUTIONARY TEXT SETTINGS - Product Information Text Configuration
    const textConfig = [
        {
            fieldName: 'buttonText',
            label: 'Button Text',
            type: 'input',
            placeholder: 'Get Instant Access',
            fontField: 'buttonFont',
            fontType: 'small',
            buttonEffectField: 'buttonEffect',
            showToggleField: 'showButtonText'
        },
        {
            fieldName: 'buttonLink',
            label: 'Custom Button Link (optional)',
            type: 'input',
            placeholder: 'https://example.com',
            showToggleField: 'showButtonLink'
        }
    ];

    // Color Configuration
    const colorConfig = [
        [
            { fieldName: 'titleColor', label: 'Title', defaultValue: '#1e293b', icon: '/img/text-fat.svg' },
            { fieldName: 'descriptionColor', label: 'Description', defaultValue: '#64748b', icon: '/img/text.svg' },
            { fieldName: 'priceColor', label: 'Price', defaultValue: '#059669', icon: '/img/text-fat.svg' }
        ],
        [
            { fieldName: 'subtypeColor', label: 'Product Type', defaultValue: '#8b5cf6', icon: '/img/text.svg' },
            { fieldName: 'featuresColor', label: 'Features Text', defaultValue: '#374151', icon: '/img/text.svg' },
            { fieldName: 'featuresIconColor', label: 'Features Icons', defaultValue: '#10b981', icon: '/img/background.svg' }
        ],
        [
            { fieldName: 'backgroundColor', label: 'Background', defaultValue: '#ffffff', icon: '/img/background.svg' }
        ],
        [
            { fieldName: 'buttonColor', label: 'Button', defaultValue: '#667eea', icon: '/img/background.svg' },
            { fieldName: 'buttonTextColor', label: 'Button Text', defaultValue: '#ffffff', icon: '/img/text.svg' },
            { fieldName: 'heroOverlayColor', label: 'Hero Overlay', defaultValue: 'rgba(0, 0, 0, 0.4)', icon: '/img/background.svg' }
        ]
    ];

    // Font Configuration
    const fontConfig = [
        { 
            label: 'Title', 
            sizeField: 'titleSize', 
            weightField: 'titleWeight', 
            defaultSize: '32', 
            defaultWeight: '700',
            fontField: 'titleFont',
            fontType: 'big',
            effectField: 'titleEffect'
        },
        { 
            label: 'Description', 
            sizeField: 'descriptionSize', 
            weightField: 'descriptionWeight', 
            defaultSize: '18', 
            defaultWeight: '400',
            fontField: 'descriptionFont',
            fontType: 'small',
            effectField: 'descriptionEffect'
        },
        { 
            label: 'Price', 
            sizeField: 'priceSize', 
            weightField: 'priceWeight', 
            defaultSize: '28', 
            defaultWeight: '700',
            fontField: 'priceFont',
            fontType: 'big'
        }
    ];

    // Layout Configuration
    const layoutConfig = [
        { fieldName: 'padding', label: 'Padding', defaultValue: '40', icon: '/img/element.svg' },
        { fieldName: 'borderRadius', label: 'Border Radius', defaultValue: '12', icon: '/img/element.svg' },
        { fieldName: 'height', label: 'Hero Height', defaultValue: '500', icon: '/img/element.svg' },
        { fieldName: 'marginTop', label: 'Space Top', defaultValue: '0', icon: '/img/element.svg' },
        { fieldName: 'marginBottom', label: 'Space Bottom', defaultValue: '40', icon: '/img/element.svg', iconStyle: { rotate: '180deg' } }
    ];

    // Get current selected product info
    const selectedProduct = validProducts.find(p => p.id === editData.productId);
    
    // Check if selected product is a free digital product
    const isFreeDigitalProduct = selectedProduct && 
                                 selectedProduct.type === 'digital' && 
                                 (selectedProduct.price === 0 || selectedProduct.price === '0');


    return (
        <div>
            <div className="top-widget-edit-container">
                
                {/* Product Selection */}
                <div className="widget-trenner">
                    <h3 style={{ marginBottom: '10px' }}>Product Connection</h3>
                </div>

                <div className="input-group">
                   
                    <select 
                        name="productId" 
                        value={editData.productId || ''} 
                        onChange={handleInputChange}
                        className="widgetInputField"
                    >
                        <option value="">Select Digital Product or Course...</option>
                        {validProducts.map(product => {
                            const typeInfo = getProductTypeInfo(product.type);
                            return (
                                <option key={product.id} value={product.id}>
                                    {typeInfo.icon} {product.title} ({product.type})
                                </option>
                            );
                        })}
                    </select>
                    
            

                    {/* Free Digital Product Info */}
                    {isFreeDigitalProduct && (
                        <div style={{
                            marginTop: '12px',
                            padding: '12px',
                            background: '#ecfdf5',
                            borderRadius: '8px',
                            border: '1px solid #10b981'
                        }}>
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '8px',
                                fontSize: '14px',
                                fontWeight: '600',
                                color: '#059669'
                            }}>
                                Free Digital Product
                            </div>
                            <div style={{
                                fontSize: '12px',
                                color: '#047857',
                                marginTop: '4px',
                                lineHeight: '1.4'
                            }}>
                                Form popup will be shown instead of payment
                            </div>
                        </div>
                    )}

                </div>



                {/* Product Image */}
                <div className="widget-trenner">
                    <h3>Product Image</h3>
                    <hr className="hrrrr" />
                </div>

                <div className="input-group" style={{ marginBottom: '0px' }}>
                    <ToggleSwitch
                        label="Show Product Image"
                        fieldName="showProductImage"
                        checked={editData.showProductImage !== false}
                        onChange={handleInputChange}
                    />
                </div>

                {editData.showProductImage !== false && (
                    <>
                        <ImageUploadSection
                            label="Product Image"
                            fieldName="productImage"
                            currentImage={editData.productImage}
                            onUpload={uploadImage}
                            isUploading={isUploadingProduct}
                            setIsUploading={setIsUploadingProduct}
                        />

                        {/* Image Mode Selector - Visual */}
                        <div className="input-group">
                            <label className="widget-label" style={{ marginBottom: '12px' }}>
                                <img src="/img/element.svg" alt="" className="label-icon" />
                                Image Display Mode
                            </label>
                            
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '12px',
                                marginBottom: '16px'
                            }}>
                                {[
                                    { 
                                        value: 'normal', 
                                        name: 'Normal Layout', 
                                        icon: '🖼️',
                                        preview: '┌─────┬───┐\n│ TXT │IMG│\n│     │   │\n└─────┴───┘',
                                        description: 'Image positioned alongside content'
                                    },
                                    { 
                                        value: 'banner', 
                                        name: 'Banner Mode', 
                                        icon: '🎨',
                                        preview: '┌─────────┐\n│ ┌─┐ TXT │\n│ │I│     │\n│ └─┘     │\n└─────────┘',
                                        description: 'Full-height side banner'
                                    }
                                ].map(mode => (
                                    <div
                                        key={mode.value}
                                        onClick={() => {
                                            const fakeEvent = {
                                                target: { name: 'productImageMode', value: mode.value }
                                            };
                                            handleInputChange(fakeEvent);
                                        }}
                                        style={{
                                            padding: '16px',
                                            borderRadius: '12px',
                                            cursor: 'pointer',
                                            border: editData.productImageMode === mode.value || (!editData.productImageMode && mode.value === 'normal') ? '2px solid #667eea' : '2px solid #e2e8f0',
                                            background: editData.productImageMode === mode.value || (!editData.productImageMode && mode.value === 'normal') ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.05))' : '#ffffff',
                                            transition: 'all 0.3s ease',
                                            position: 'relative',
                                            boxShadow: editData.productImageMode === mode.value || (!editData.productImageMode && mode.value === 'normal') ? '0 4px 12px rgba(102, 126, 234, 0.15)' : '0 2px 4px rgba(0, 0, 0, 0.05)'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (editData.productImageMode !== mode.value && !(mode.value === 'normal' && !editData.productImageMode)) {
                                                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(102, 126, 234, 0.05), rgba(118, 75, 162, 0.02))';
                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                                e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.1)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (editData.productImageMode !== mode.value && !(mode.value === 'normal' && !editData.productImageMode)) {
                                                e.currentTarget.style.background = '#ffffff';
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                                            }
                                        }}
                                    >
                                        {/* Mode Preview */}
                                        <div style={{
                                            width: '100%',
                                            height: '60px',
                                            borderRadius: '8px',
                                            background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                                            marginBottom: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '8px',
                                            fontFamily: 'monospace',
                                            color: '#64748b',
                                            fontWeight: '600',
                                            whiteSpace: 'pre-line',
                                            textAlign: 'center',
                                            lineHeight: '1.2',
                                            border: '1px solid rgba(102, 126, 234, 0.1)',
                                            position: 'relative',
                                            overflow: 'hidden'
                                        }}>
                                            {/* Icon Overlay */}
                                            <div style={{
                                                position: 'absolute',
                                                top: '8px',
                                                right: '8px',
                                                fontSize: '16px',
                                                opacity: 0.7
                                            }}>
                                                {mode.icon}
                                            </div>
                                            {mode.preview}
                                        </div>
                                        
                                        {/* Mode Info */}
                                        <div style={{ textAlign: 'center' }}>
                                            <div style={{
                                                fontSize: '13px',
                                                fontWeight: '600',
                                                color: editData.productImageMode === mode.value || (!editData.productImageMode && mode.value === 'normal') ? '#667eea' : '#374151',
                                                marginBottom: '4px'
                                            }}>
                                                {mode.name}
                                            </div>
                                            <div style={{
                                                fontSize: '11px',
                                                color: '#64748b',
                                                lineHeight: '1.3'
                                            }}>
                                                {mode.description}
                                            </div>
                                        </div>
                                        
                                        {/* Selected Indicator */}
                                        {(editData.productImageMode === mode.value || (!editData.productImageMode && mode.value === 'normal')) && (
                                            <div style={{
                                                position: 'absolute',
                                                top: '8px',
                                                left: '8px',
                                                width: '20px',
                                                height: '20px',
                                                borderRadius: '50%',
                                                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '10px',
                                                color: 'white',
                                                fontWeight: '700',
                                                boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
                                            }}>
                                                ✓
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Normal Mode Settings */}
                        {editData.productImageMode !== 'banner' && (
                            <>
                                {/* Image Position Selector - Visual */}
                                <div className="input-group">
                                    <label className="widget-label" style={{ marginBottom: '12px' }}>
                                        <img src="/img/element.svg" alt="" className="label-icon" />
                                        Image Position
                                    </label>
                                    
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr',
                                        gap: '12px',
                                        marginBottom: '16px'
                                    }}>
                                        {[
                                            { 
                                                value: 'left', 
                                                name: 'Left', 
                                                icon: '⬅️',
                                                preview: '┌───┬─────┐\n│IMG│ TXT │\n│   │     │\n└───┴─────┘'
                                            },
                                            { 
                                                value: 'right', 
                                                name: 'Right', 
                                                icon: '➡️',
                                                preview: '┌─────┬───┐\n│ TXT │IMG│\n│     │   │\n└─────┴───┘'
                                            }
                                        ].map(position => (
                                            <div
                                                key={position.value}
                                                onClick={() => {
                                                    const fakeEvent = {
                                                        target: { name: 'productImagePosition', value: position.value }
                                                    };
                                                    handleInputChange(fakeEvent);
                                                }}
                                                style={{
                                                    padding: '12px 8px',
                                                    borderRadius: '8px',
                                                    cursor: 'pointer',
                                                    border: editData.productImagePosition === position.value || (!editData.productImagePosition && position.value === 'right') ? '2px solid #667eea' : '2px solid #e2e8f0',
                                                    background: editData.productImagePosition === position.value || (!editData.productImagePosition && position.value === 'right') ? 'rgba(102, 126, 234, 0.1)' : '#ffffff',
                                                    transition: 'all 0.2s ease',
                                                    position: 'relative',
                                                    textAlign: 'center'
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (editData.productImagePosition !== position.value && !(position.value === 'right' && !editData.productImagePosition)) {
                                                        e.currentTarget.style.background = 'rgba(102, 126, 234, 0.05)';
                                                        e.currentTarget.style.transform = 'translateY(-1px)';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (editData.productImagePosition !== position.value && !(position.value === 'right' && !editData.productImagePosition)) {
                                                        e.currentTarget.style.background = '#ffffff';
                                                        e.currentTarget.style.transform = 'translateY(0)';
                                                    }
                                                }}
                                            >
                                                {/* Position Preview */}
                                                <div style={{
                                                    width: '100%',
                                                    height: '40px',
                                                    borderRadius: '4px',
                                                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                                                    marginBottom: '8px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '6px',
                                                    fontFamily: 'monospace',
                                                    color: '#64748b',
                                                    fontWeight: '600',
                                                    whiteSpace: 'pre-line',
                                                    textAlign: 'center',
                                                    lineHeight: '1.1',
                                                    border: '1px solid rgba(102, 126, 234, 0.1)'
                                                }}>
                                                    {position.preview}
                                                </div>
                                                
                                                {/* Position Icon & Name */}
                                                <div style={{
                                                    fontSize: '12px',
                                                    marginBottom: '2px'
                                                }}>
                                                    {position.icon}
                                                </div>
                                                <div style={{
                                                    fontSize: '10px',
                                                    fontWeight: '600',
                                                    color: editData.productImagePosition === position.value || (!editData.productImagePosition && position.value === 'right') ? '#667eea' : '#64748b'
                                                }}>
                                                    {position.name}
                                                </div>
                                                
                                                {/* Selected Indicator */}
                                                {(editData.productImagePosition === position.value || (!editData.productImagePosition && position.value === 'right')) && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: '4px',
                                                        right: '4px',
                                                        width: '14px',
                                                        height: '14px',
                                                        borderRadius: '50%',
                                                        background: '#667eea',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '8px',
                                                        color: 'white',
                                                        fontWeight: '700'
                                                    }}>
                                                        ✓
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                            </>
                        )}

                        {/* Banner Mode Settings */}
                        {editData.productImageMode === 'banner' && (
                            <>
                                <div className="input-group">
                                    <label className="widget-label">
                                        <img src="/img/element.svg" alt="" className="label-icon" />
                                        Banner Position
                                    </label>
                                    <select 
                                        name="bannerImageAlignment" 
                                        value={editData.bannerImageAlignment || 'right'} 
                                        onChange={handleInputChange}
                                        className="widgetInputField"
                                    >
                                        <option value="right">Right Side</option>
                                        <option value="left">Left Side</option>
                                    </select>
                                </div>

                                <div className="input-group">
                                    <label className="widget-label">
                                        <img src="/img/element.svg" alt="" className="label-icon" />
                                        Banner Width (%)
                                    </label>
                                    <input
                                        type="number"
                                        name="bannerImageWidth"
                                        value={editData.bannerImageWidth || 40}
                                        onChange={handleInputChange}
                                        className="widgetInputField"
                                        min="20"
                                        max="60"
                                    />
                                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                                        {editData.bannerImageWidth || 40}% of container width
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Image Shape Selector - Visual */}
                        <div className="input-group">
                            <label className="widget-label" style={{ marginBottom: '12px' }}>
                                <img src="/img/element.svg" alt="" className="label-icon" />
                                Image Shape
                            </label>
                            
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, 1fr)',
                                gap: '12px',
                                marginBottom: '16px'
                            }}>
                                {[
                                    { 
                                        value: 'rectangle', 
                                        name: 'Rectangle', 
                                        icon: '⬜',
                                        description: 'Sharp corners',
                                        borderRadius: '0px'
                                    },
                                    { 
                                        value: 'rounded', 
                                        name: 'Rounded', 
                                        icon: '▢',
                                        description: 'Custom radius',
                                        borderRadius: '12px'
                                    },
                                    { 
                                        value: 'circle', 
                                        name: 'Circle', 
                                        icon: '⭕',
                                        description: 'Perfect round',
                                        borderRadius: '50%'
                                    }
                                ].map(shape => (
                                    <div
                                        key={shape.value}
                                        onClick={() => {
                                            const fakeEvent = {
                                                target: { name: 'productImageShape', value: shape.value }
                                            };
                                            handleInputChange(fakeEvent);
                                        }}
                                        style={{
                                            padding: '16px',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            border: editData.productImageShape === shape.value || (!editData.productImageShape && shape.value === 'rounded') ? '2px solid #667eea' : '2px solid #e2e8f0',
                                            background: editData.productImageShape === shape.value || (!editData.productImageShape && shape.value === 'rounded') ? 'rgba(102, 126, 234, 0.1)' : '#ffffff',
                                            transition: 'all 0.2s ease',
                                            position: 'relative',
                                            textAlign: 'center'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (editData.productImageShape !== shape.value && !(shape.value === 'rounded' && !editData.productImageShape)) {
                                                e.currentTarget.style.background = 'rgba(102, 126, 234, 0.05)';
                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (editData.productImageShape !== shape.value && !(shape.value === 'rounded' && !editData.productImageShape)) {
                                                e.currentTarget.style.background = '#ffffff';
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.boxShadow = 'none';
                                            }
                                        }}
                                    >
                                        {/* Shape Preview */}
                                        <div style={{
                                            width: '50px',
                                            height: '50px',
                                            margin: '0 auto 12px',
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            borderRadius: shape.borderRadius,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '20px',
                                            color: 'white',
                                            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                                            position: 'relative',
                                            overflow: 'hidden'
                                        }}>
                                            {/* Shimmer Effect */}
                                            <div style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: '-100%',
                                                width: '100%',
                                                height: '100%',
                                                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                                                animation: 'shimmer 2s infinite',
                                                borderRadius: shape.borderRadius
                                            }} />
                                            📷
                                        </div>
                                        
                                        {/* Shape Info */}
                                        <div style={{
                                            fontSize: '12px',
                                            fontWeight: '600',
                                            color: editData.productImageShape === shape.value || (!editData.productImageShape && shape.value === 'rounded') ? '#667eea' : '#374151',
                                            marginBottom: '4px'
                                        }}>
                                            {shape.name}
                                        </div>
                                        <div style={{
                                            fontSize: '10px',
                                            color: '#64748b',
                                            lineHeight: '1.2'
                                        }}>
                                            {shape.description}
                                        </div>
                                        
                                        {/* Selected Indicator */}
                                        {(editData.productImageShape === shape.value || (!editData.productImageShape && shape.value === 'rounded')) && (
                                            <div style={{
                                                position: 'absolute',
                                                top: '8px',
                                                right: '8px',
                                                width: '16px',
                                                height: '16px',
                                                borderRadius: '50%',
                                                background: '#667eea',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '10px',
                                                color: 'white',
                                                fontWeight: '700',
                                                boxShadow: '0 2px 6px rgba(102, 126, 234, 0.4)'
                                            }}>
                                                ✓
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                            
                            {/* Shimmer Animation */}
                            <style>
                                {`
                                    @keyframes shimmer {
                                        0% { left: -100%; }
                                        100% { left: 100%; }
                                    }
                                `}
                            </style>
                        </div>

                        {/* Size Settings (Normal Mode only) */}
                        {editData.productImageMode !== 'banner' && (
                            <>
                                <div className="input-group">
                                    <label className="widget-label">
                                        <img src="/img/element.svg" alt="" className="label-icon" />
                                        Image Width (px)
                                    </label>
                                    <input
                                        type="number"
                                        name="productImageWidth"
                                        value={editData.productImageWidth || 300}
                                        onChange={handleInputChange}
                                        className="widgetInputField"
                                        min="100"
                                        max="800"
                                    />
                                </div>

                                <div className="input-group">
                                    <label className="widget-label">
                                        <img src="/img/element.svg" alt="" className="label-icon" />
                                        Image Height (px, optional)
                                    </label>
                                    <input
                                        type="number"
                                        name="productImageHeight"
                                        value={editData.productImageHeight || ''}
                                        onChange={handleInputChange}
                                        className="widgetInputField"
                                        min="100"
                                        max="600"
                                        placeholder="Auto height"
                                    />
                                </div>
                            </>
                        )}

                        {/* Border Radius (nur für rounded shape) */}
                        {editData.productImageShape === 'rounded' && (
                            <div className="input-group">
                                <label className="widget-label">
                                    <img src="/img/element.svg" alt="" className="label-icon" />
                                    Image Border Radius
                                </label>
                                <input
                                    type="number"
                                    name="productImageBorderRadius"
                                    value={editData.productImageBorderRadius || 12}
                                    onChange={handleInputChange}
                                    className="widgetInputField"
                                    min="0"
                                    max="50"
                                />
                            </div>
                        )}
                    </>
                )}

                {/* Background Image */}
                <div className="widget-trenner">
                    <h3>Background Image</h3>
                </div>

                <ImageUploadSection
                    label=""
                    fieldName="backgroundImage"
                    currentImage={editData.backgroundImage}
                    onUpload={uploadImage}
                    isUploading={isUploadingBackground}
                    setIsUploading={setIsUploadingBackground}
                />

                {/* Background Image Effects (nur wenn Background Image vorhanden) */}
                {editData.backgroundImage && (
                    <>
                        <div className="widget-trenner">
                        </div>

                        <div className="input-group" style={{ marginTop: '25px' }}>
                            <ImageEffectSelector
                                selectedEffect={editData.imageEffect || 'none'}
                                onEffectChange={(effectId) => handleEffectChange('imageEffect', effectId)}
                            />
                        </div>

                        {/* Background Image Opacity Slider */}
                        <SliderSection
                            label="Background Opacity"
                            fieldName="backgroundImageOpacity"
                            value={(editData.backgroundImageOpacity || 0.8) * 100}
                            onChange={(fieldName, value) => handleSliderChange(fieldName, value / 100)}
                            min={10}
                            max={100}
                            step={10}
                            unit="%"
                            icon="/img/element.svg"
                            color="#9C27B0"
                            description="Opacity of the background image"
                        />
                    </>
                )}

                {/* Display Options */}
                <div className="widget-trenner">
                    <h3>Display Options</h3>
                    <hr style={{ marginBottom: '15px' }} className="hrrrr" />
                </div>

                {/* Display Options - Compact for 220px width */}
                <div className="display-options-grid" style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '8px',
                    marginBottom: '16px',
                    padding: '0',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05), rgba(118, 75, 162, 0.05))',
                    borderRadius: '10px',
                    border: '1px solid rgba(102, 126, 234, 0.1)'
                }}>
                    <div className="display-option-item" style={{
                        padding: '8px',
                        background: 'rgba(255, 255, 255, 0.8)',
                        borderRadius: '8px',
                        border: '1px solid rgba(102, 126, 234, 0.1)',
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <div style={{ 
                            fontSize: '10px', 
                            fontWeight: '600', 
                            color: '#667eea', 
                            marginBottom: '6px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                        }}>
                            Title
                        </div>
                    <ToggleSwitch
                            label=""
                        fieldName="showTitle"
                        checked={editData.showTitle !== false}
                        onChange={handleInputChange}
                    />
                    </div>
                    
                    <div className="display-option-item" style={{
                        padding: '8px',
                        background: 'rgba(255, 255, 255, 0.8)',
                        borderRadius: '8px',
                        border: '1px solid rgba(102, 126, 234, 0.1)',
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <div style={{ 
                            fontSize: '10px', 
                            fontWeight: '600', 
                            color: '#667eea', 
                            marginBottom: '6px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                        }}>
                            Desc
                        </div>
                    <ToggleSwitch
                            label=""
                        fieldName="showDescription"
                        checked={editData.showDescription !== false}
                        onChange={handleInputChange}
                    />
                    </div>
                    
                    <div className="display-option-item" style={{
                        padding: '8px',
                        background: 'rgba(255, 255, 255, 0.8)',
                        borderRadius: '8px',
                        border: '1px solid rgba(102, 126, 234, 0.1)',
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <div style={{ 
                            fontSize: '10px', 
                            fontWeight: '600', 
                            color: '#667eea', 
                            marginBottom: '6px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                        }}>
                            Type
                        </div>
                    <ToggleSwitch
                            label=""
                        fieldName="showSubtype"
                        checked={editData.showSubtype !== false}
                        onChange={handleInputChange}
                    />
                    </div>
                    
                    <div className="display-option-item" style={{
                        padding: '8px',
                        background: 'rgba(255, 255, 255, 0.8)',
                        borderRadius: '8px',
                        border: '1px solid rgba(102, 126, 234, 0.1)',
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <div style={{ 
                            fontSize: '10px', 
                            fontWeight: '600', 
                            color: '#667eea', 
                            marginBottom: '6px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                        }}>
                            Price
                        </div>
                    <ToggleSwitch
                            label=""
                        fieldName="showPrice"
                        checked={editData.showPrice !== false}
                        onChange={handleInputChange}
                    />
                    </div>

                    <div className="display-option-item" style={{
                        padding: '8px',
                        background: 'rgba(255, 255, 255, 0.8)',
                        borderRadius: '8px',
                        border: '1px solid rgba(102, 126, 234, 0.1)',
                        textAlign: 'center',
                        gridColumn: 'span 2',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <div style={{ 
                            fontSize: '10px', 
                            fontWeight: '600', 
                            color: '#667eea', 
                            marginBottom: '6px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                        }}>
                            Button
                        </div>
                    <ToggleSwitch
                            label=""
                        fieldName="showButton"
                        checked={editData.showButton !== false}
                        onChange={handleInputChange}
                    />
                    </div>
                </div>

                    {/* Discount Display Options - Only show if product has discount AND is paid */}
                    {hasDiscount && selectedProduct && selectedProduct.price > 0 && (
                        <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                            <h4 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                                Discount Display Options
                            </h4>
                            
                            {/* Secondary Discount Badge Toggle - Only for paid products with discount */}
                            <div className="input-group" style={{ marginBottom: '16px' }}>
                                <ToggleSwitch
                                    label="Secondary Discount Badge"
                                    fieldName="showDiscountBadge"
                                    checked={editDataWithDefaults.showDiscountBadge !== false}
                                    onChange={handleInputChange}
                                />
                                <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px' }}>
                                    Shows discount badge and pricing information within the widget
                                </small>
                            </div>
                        </div>
                    )}

                        {/* Button Product Link */}
                        <div className="widget-trenner" style={{ marginTop: '20px' }}>
                            <h3>Button Product Link</h3>
                            <hr className="hrrrr" />
                    </div>

                        <div className="input-group">
                       
                            <select 
                                name="buttonProductId" 
                                value={editData.buttonProductId || ''}
                                onChange={handleInputChange}
                                className="widgetInputField"
                            >
                                <option value="">Use current product or custom link</option>
                                {validProducts.map(product => {
                                    const typeInfo = getProductTypeInfo(product.type);
                                    const isFree = product.price === 0 || product.price === '0';
                                    const priceText = isFree ? 'Free' : `€${product.price}`;
                                    return (
                                        <option key={product.id} value={product.id}>
                                            {typeInfo.icon} {product.title} ({priceText})
                                        </option>
                                    );
                                })}
                            </select>
                        </div>

                        {editData.buttonProductId && (
                            <div style={{
                                padding: '12px',
                                backgroundColor: '#f0f9ff',
                                borderRadius: '6px',
                                border: '1px solid #0ea5e9',
                                fontSize: '13px',
                                color: '#0369a1',
                                marginTop: '-15px'
                            }}>
                                {(() => {
                                    const linkedProduct = validProducts.find(p => p.id === editData.buttonProductId);
                                    if (!linkedProduct) return '📝 Product not found';
                                    
                                    const isFree = linkedProduct.price === 0 || linkedProduct.price === '0';
                                    if (isFree) {
                                        return '📝 Button will show form popup for this free product';
                                    } else if (linkedProduct.stripePriceId) {
                                        return '💳 Button will redirect to Stripe checkout for this paid product';
                                    } else {
                                        return '🔗 Button will use custom link (no Stripe configured)';
                                    }
                                })()}
                            </div>
                        )}
                    </div>

                {/* Custom Features */}
                <div className="widget-trenner">
                    <h3>Custom Features</h3>
                    <hr className="hrrrr" />
                </div>

                <div className="input-group">
                    <ToggleSwitch
                        label="Show Custom Features"
                        fieldName="showCustomFeatures"
                        checked={editData.showCustomFeatures !== false}
                        onChange={handleInputChange}
                    />
                    
                    {editData.showCustomFeatures !== false && (
                        <>
                            {/* Feature Display Style - Visual */}
                            <div className="input-group" style={{ marginTop: '16px' }}>
                                <label className="widget-label" style={{ marginBottom: '12px' }}>
                                    <img src="/img/element.svg" alt="" className="label-icon" />
                                    Feature Display Style
                                </label>
                                
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(3, 1fr)',
                                    gap: '12px',
                                    marginBottom: '16px'
                                }}>
                                    {[
                                        { 
                                            value: 'pills', 
                                            name: 'Pills', 
                                            description: 'Rounded background',
                                            preview: '● Feature 1\n● Feature 2'
                                        },
                                        { 
                                            value: 'minimal', 
                                            name: 'Minimal', 
                                            description: 'Clean & simple',
                                            preview: '✓ Feature 1\n✓ Feature 2'
                                        },
                                        { 
                                            value: 'lines', 
                                            name: 'Lines', 
                                            description: 'Full width lines',
                                            preview: '✓ Feature 1\n────────\n✓ Feature 2'
                                        },
                                        { 
                                            value: 'bullets', 
                                            name: 'Bullets', 
                                            description: 'Classic bullets',
                                            preview: '● Feature 1\n● Feature 2'
                                        },
                                        { 
                                            value: 'cards', 
                                            name: 'Cards', 
                                            description: 'Individual cards',
                                            preview: '┌─────────┐\n│✓Feature1│\n└─────────┘'
                                        },
                                        { 
                                            value: 'badges', 
                                            name: 'Badges', 
                                            description: 'Colorful badges',
                                            preview: '✓ Feature 1\n✓ Feature 2'
                                        }
                                    ].map(style => (
                                        <div
                                            key={style.value}
                                            onClick={() => {
                                                const fakeEvent = {
                                                    target: { name: 'featureStyle', value: style.value }
                                                };
                                                handleInputChange(fakeEvent);
                                            }}
                                            style={{
                                                padding: '14px',
                                                borderRadius: '10px',
                                                cursor: 'pointer',
                                                border: editData.featureStyle === style.value || (!editData.featureStyle && style.value === 'pills') ? '2px solid #667eea' : '2px solid #e2e8f0',
                                                background: editData.featureStyle === style.value || (!editData.featureStyle && style.value === 'pills') ? 'rgba(102, 126, 234, 0.1)' : '#ffffff',
                                                transition: 'all 0.2s ease',
                                                position: 'relative',
                                                textAlign: 'center'
                                            }}
                                            onMouseEnter={(e) => {
                                                if (editData.featureStyle !== style.value && !(style.value === 'pills' && !editData.featureStyle)) {
                                                    e.currentTarget.style.background = 'rgba(102, 126, 234, 0.05)';
                                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (editData.featureStyle !== style.value && !(style.value === 'pills' && !editData.featureStyle)) {
                                                    e.currentTarget.style.background = '#ffffff';
                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                }
                                            }}
                                        >
                                            {/* Style Preview */}
                                            <div style={{
                                                width: '100%',
                                                height: '50px',
                                                borderRadius: '6px',
                                                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                                                marginBottom: '10px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '7px',
                                                fontFamily: 'monospace',
                                                color: '#64748b',
                                                fontWeight: '600',
                                                whiteSpace: 'pre-line',
                                                textAlign: 'center',
                                                lineHeight: '1.1',
                                                border: '1px solid rgba(102, 126, 234, 0.1)',
                                                position: 'relative'
                                            }}>
                                                {style.preview}
                                            </div>
                                            
                                            {/* Style Info */}
                                            <div style={{
                                                fontSize: '11px',
                                                fontWeight: '600',
                                                color: editData.featureStyle === style.value || (!editData.featureStyle && style.value === 'pills') ? '#667eea' : '#374151',
                                                marginBottom: '2px'
                                            }}>
                                                {style.name}
                                            </div>
                                            <div style={{
                                                fontSize: '9px',
                                                color: '#64748b',
                                                lineHeight: '1.2'
                                            }}>
                                                {style.description}
                                            </div>
                                            
                                            {/* Selected Indicator */}
                                            {(editData.featureStyle === style.value || (!editData.featureStyle && style.value === 'pills')) && (
                                                <div style={{
                                                    position: 'absolute',
                                                    top: '6px',
                                                    left: '6px',
                                                    width: '16px',
                                                    height: '16px',
                                                    borderRadius: '50%',
                                                    background: '#667eea',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '9px',
                                                    color: 'white',
                                                    fontWeight: '700'
                                                }}>
                                                    ✓
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <label className="widget-label" style={{ marginTop: '16px', marginBottom: '8px' }}>
                                Custom Features (max 5)
                            </label>
                            
                            {/* Custom Features List */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
                                {(editData.customFeatures || []).map((feature, index) => (
                                    <div key={index} style={{
                                        display: 'flex',
                                        gap: '12px',
                                        padding: '14px 16px',
                                        background: 'rgba(255, 255, 255, 0.8)',
                                        borderRadius: '12px',
                                        border: '1px solid rgba(226, 232, 240, 0.6)',
                                        alignItems: 'center',
                                        transition: 'all 0.2s ease',
                                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                                    }}>
                                        <input
                                            type="text"
                                            placeholder="Enter feature description..."
                                            value={feature.text || ''}
                                            onChange={(e) => {
                                                const newFeatures = [...(editData.customFeatures || [])];
                                                newFeatures[index] = { text: e.target.value };
                                                const updatedEditData = { ...editData, customFeatures: newFeatures };
                                                customOnUpdate(updatedEditData);
                                            }}
                                            className="widgetInputField2"
                                            style={{ 
                                                flex: 1,
                                                border: 'none',
                                                background: 'transparent',
                                                fontSize: '14px',
                                                color: '#374151',
                                                outline: 'none',
                                                marginLeft: '-30px !important',
                                                outline: 'none',

                                            }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const newFeatures = (editData.customFeatures || []).filter((_, i) => i !== index);
                                                const updatedEditData = { ...editData, customFeatures: newFeatures };
                                                customOnUpdate(updatedEditData);
                                            }}
                                            style={{
                                                width: '28px',
                                                height: '28px',
                                                border: 'none',
                                                background: 'rgba(239, 68, 68, 0.1)',
                                                borderRadius: '100px',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '14px',
                                                color: '#ef4444',
                                                transition: 'all 0.3s ease',
                                                flexShrink: 0
                                            }}
                                            onMouseEnter={(e) => {
                                                e.target.style.background = 'rgba(239, 68, 68, 0.2)';
                                                e.target.style.transform = 'scale(1.1)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.background = 'rgba(239, 68, 68, 0.1)';
                                                e.target.style.transform = 'scale(1)';
                                            }}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ))}
                            </div>
                            
                            {/* Add Feature Button */}
                            {(!editData.customFeatures || editData.customFeatures.length < 5) && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        const currentFeatures = editData.customFeatures || [];
                                        const newFeatures = [...currentFeatures, { text: '' }];
                                        const updatedEditData = { ...editData, customFeatures: newFeatures };
                                        customOnUpdate(updatedEditData);
                                    }}
                                    style={{
                                        background: 'rgba(102, 126, 234, 0.08)',
                                        color: '#667eea',
                                        border: '1px dashed rgba(102, 126, 234, 0.3)',
                                        borderRadius: '8px',
                                        padding: '12px 16px',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        fontWeight: '500',
                                        transition: 'all 0.3s ease',
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.background = 'rgba(102, 126, 234, 0.12)';
                                        e.target.style.borderColor = 'rgba(102, 126, 234, 0.4)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.background = 'rgba(102, 126, 234, 0.08)';
                                        e.target.style.borderColor = 'rgba(102, 126, 234, 0.3)';
                                    }}
                                >
                                    <span style={{ fontSize: '16px', fontWeight: '700' }}>+</span>
                                    Add Feature ({(editData.customFeatures || []).length}/5)
                                </button>
                            )}
                        </>
                    )}
                </div>

                {/* Button Configuration */}
                {editData.showButton !== false && (
                    <>
                        <div className="widget-trenner">
                            <h3>Button Settings</h3>
                            <hr className="hrrrr" />
                        </div>

                        {/* 🚀 REVOLUTIONARY TEXT SETTINGS - Product Information Widget */}
                        <TextSettingsSection 
                            textConfig={textConfig.filter(item => {
                                // Hide buttonLink if Stripe checkout is enabled OR if a product is linked
                                if (item.fieldName === 'buttonLink') {
                                    return editData.useStripeCheckout !== true && !editData.buttonProductId;
                                }
                                return true;
                            })} 
                            editData={editData} 
                            handleInputChange={handleInputChange}
                            showFontSelector={true}
                            showTextEffects={true}
                            showButtonEffects={true}
                        />

                        <div className="input-group">
                            <label className="widget-label">
                                <img src="/img/element.svg" alt="" className="label-icon" />
                                Button Border Radius
                            </label>
                            <input
                                type="number"
                                name="buttonBorderRadius"
                                value={editData.buttonBorderRadius || 8}
                                onChange={handleInputChange}
                                className="widgetInputField"
                                min="0"
                                max="50"
                            />
                        </div>

                        <div className="input-group">
                            <label className="widget-label">
                                <img src="/img/element.svg" alt="" className="label-icon" />
                                Button Padding Y
                            </label>
                            <input
                                type="number"
                                name="buttonPaddingY"
                                value={editData.buttonPaddingY || 16}
                                onChange={handleInputChange}
                                className="widgetInputField"
                                min="0"
                                max="50"
                            />
                        </div>

                        <div className="input-group">
                            <label className="widget-label">
                                <img src="/img/element.svg" alt="" className="label-icon" />
                                Button Padding X
                            </label>
                            <input
                                type="number"
                                name="buttonPaddingX"
                                value={editData.buttonPaddingX || 32}
                                onChange={handleInputChange}
                                className="widgetInputField"
                                min="0"
                                max="100"
                            />
                        </div>
                        
                        {/* Stripe Payment Toggle - Hidden for free products */}
                        {!isFreeDigitalProduct && (
                        <div className="input-group">
                            <ToggleSwitch
                                label="Show Stripe Payment"
                                fieldName="showStripePayment"
                                checked={editData.showStripePayment !== false}
                                onChange={handleInputChange}
                            />
                        </div>
                        )}

                        {/* Stripe Checkout Toggle - Hidden for free products */}
                        {!isFreeDigitalProduct && (
                        <div className="input-group">
                            <ToggleSwitch
                                label="Use Stripe Checkout"
                                fieldName="useStripeCheckout"
                                checked={editData.useStripeCheckout !== false}
                                onChange={handleInputChange}
                                helpText="Enable direct Stripe payment processing (requires product Stripe configuration)"
                            />
                        </div>
                        )}
                    </>
                )}


                {/* More Settings - Colors, Fonts, Layout */}
                <MoreSettingsSection 
                    colors={colors}
                    colorConfig={colorConfig}
                    fontConfig={fontConfig}
                    layoutConfig={layoutConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    showFontSelector={true}
                    showTextEffects={true}
                    showButtonEffects={true}
                />

        </div>
    );
}

export default ProductInfoWidgetEdit;